/**
 * VerooglePage (`/demos/veroogle`)
 *
 * First client-specific selling demo. Phase 1 (cards T1010cr → T1040cr)
 * is fully interactive against mocked merchant-services data. Phases
 * 2-5 render as compact "listening" stubs so the client sees the full
 * NEXENGINES platform roadmap without us faking logic we haven't built.
 *
 * All actions are sandboxed via the parent <DemoModeProvider> — nothing
 * writes to the DB. Native card IDs (T1010cr → T6200cr) are preserved
 * exactly as spec'd in AGENT DIRECTIVE: CLIENT DEMO SCOPE.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import SoundGate from "@/components/clientDemo/tour/SoundGate";
import axios from "axios";
import { Plus } from "lucide-react";
import AccordionSection from "@/components/nds/AccordionSection";
import { DemoModeProvider } from "@/lib/demoMode";
import { GuidedTourProvider } from "@/lib/guidedTour";
import { API } from "@/lib/auth";
import LeadScrapingCard from "@/components/clientDemo/LeadScrapingCard";
import LeadScoringCard from "@/components/clientDemo/LeadScoringCard";
import LeadPipelineCard from "@/components/clientDemo/LeadPipelineCard";
import StatementAnalyzerCard from "@/components/clientDemo/StatementAnalyzerCard";
import PhaseStubCard from "@/components/clientDemo/PhaseStubCard";
import VerooglMetricsBar from "@/components/clientDemo/VerooglMetricsBar";
import TourControlStrip from "@/components/clientDemo/tour/TourControlStrip";
import TourOverlay from "@/components/clientDemo/tour/TourOverlay";
import TourActionRunner from "@/components/clientDemo/tour/TourActionRunner";
import { VEROOGLE_TOUR_SCRIPT, tourAudioUrl } from "@/components/clientDemo/tour/verooglTourScript";
import CinematicOutro from "@/components/clientDemo/outro/CinematicOutro";
import { DemoBrandProvider, brandText } from "@/lib/demoBrand";
import { scoreLead, tierOf } from "@/components/clientDemo/scoring";
import { resolveIcon } from "@/components/clientDemo/verooglIcons";
import CardEditModal from "@/components/clientDemo/CardEditModal";
import CinematicIntro from "@/components/clientDemo/intro/CinematicIntro";
import IntroEditorDialog from "@/components/clientDemo/intro/IntroEditorDialog";
import HypeCard from "@/components/clientDemo/intro/HypeCard";
import IntakeCard from "@/components/clientDemo/phase2/IntakeCard";
import DocVaultCard from "@/components/clientDemo/phase2/DocVaultCard";
import KycCard from "@/components/clientDemo/phase2/KycCard";
import UnderwritingCard from "@/components/clientDemo/phase2/UnderwritingCard";

import MidMonitorCard from "@/components/clientDemo/phase3/MidMonitorCard";
import FraudCard from "@/components/clientDemo/phase3/FraudCard";
import DisputeCard from "@/components/clientDemo/phase3/DisputeCard";
import ResidualCard from "@/components/clientDemo/phase3/ResidualCard";

import { Phase4Cards, Phase5Cards } from "@/components/clientDemo/phase45/Phase45Cards";
import TourMusicBed from "@/components/clientDemo/tour/TourMusicBed";
import Phase6Cards from "@/components/clientDemo/phase6/Phase6Cards";

const INTERACTIVE_IDS = {
  2: ["T2500cr", "T2220cr", "T2300cr", "T2400cr"],
  3: ["T3100cr", "T3200cr", "T3300cr", "T4100cr"],
  4: ["T4200cr", "T4300cr", "T5100cr", "T5200cr"],
  5: ["T5300cr", "T4480cr", "T6100cr", "T6200cr"],
  6: ["T7100cr", "T7200cr", "T7300cr", "T7400cr"],
};
const RISK_WIN_VALUE = { "cb-20418": 1240, "fraud-7719": 500, "recon-186": 186, retained: 3000 };
const STAGE_RANK = { scraped: 0, contacted: 1, qualified: 2, statement: 3, won: 4 };
const DEFAULT_MERCHANT = { name: "Lakeside Oyster House", volume: 231500, addr: "2200 Olentangy, Columbus, OH", phone: "(614) 555-0135" };

const INTRO_PHASES = ["Lead Intelligence", "Onboarding", "Portfolio Risk", "Accounting", "Automation & AI", "Custom Builds"];
const PHASE_META = [
  { id: "phase1_veroogle", kicker: "// PHASE 1 · ACTIVE",      title: "Lead Intelligence & Acquisition" },
  { id: "phase2_veroogle", kicker: "// PHASE 2 · LISTENING",   title: "Onboarding & Merchant Verification",            phase: 2 },
  { id: "phase3_veroogle", kicker: "// PHASE 3 · LISTENING",   title: "Portfolio Risk & Dispute Operations",           phase: 3 },
  { id: "phase4_veroogle", kicker: "// PHASE 4 · LISTENING",   title: "Financial Accounting & Terminal Logistics",     phase: 4 },
  { id: "phase5_veroogle", kicker: "// PHASE 5 · LISTENING",   title: "Automation, Analytics & AI Support",            phase: 5 },
  { id: "phase6_veroogle", kicker: "// PHASE 6 · CUSTOM",      title: "Custom Specialty Engines",                      phase: 6 },
];

let _idSeed = 0;
const nextId = () => `l${Date.now()}_${++_idSeed}`;

export default function VerooglePage({ slug = "veroogle", businessName }) {
  const [config, setConfig] = useState(null);
  useEffect(() => {
    axios.get(`${API}/demos/${slug}/config`).then(({ data }) => setConfig(data)).catch(() => {});
  }, [slug]);
  const name = config?.business_name || businessName || "VEROOGLE";
  const accent = config?.accent_color || "#06b6d4";
  const phaseMeta = useMemo(() => PHASE_META.map((p, i) => ({ ...p, title: config?.phase_titles?.[i] || p.title })), [config]);
  const introPhases = INTRO_PHASES.map((p, i) => config?.phase_titles?.[i] || p);

  // Cross-card state: Card 1 pushes to Card 2's scoring pool, which
  // then feeds Card 3's pipeline. All lives in-memory for the demo.
  const [scoringPool, setScoringPool] = useState([]);
  const [pipeline, setPipeline] = useState([]);
  // T1020cr weights lifted up so the MetricsBar hot-count stays in
  // sync as the operator tweaks sliders.
  const [weights, setWeights] = useState({ volume: 50, risk: 20, recency: 30 });

  const pushToScoring = (lead) => {
    const id = nextId();
    setScoringPool((prev) => prev.find((p) => p.name === lead.name) ? prev : [...prev, { ...lead, id }]);
  };

  const pushToPipeline = (lead) => {
    setPipeline((prev) => prev.find((p) => p.name === lead.name)
      ? prev
      : [...prev, { ...lead, id: lead.id || nextId(), stage: "scraped" }]);
  };

  const [phase1Key, setPhase1Key] = useState(0);
  const [approved, setApproved] = useState([]);
  const [riskWins, setRiskWins] = useState([]);
  const [musicOn, setMusicOn] = useState(() => { try { return localStorage.getItem("demo_tour_music") !== "off"; } catch { return true; } });
  const toggleMusic = () => setMusicOn((m) => !m);
  useEffect(() => { try { localStorage.setItem("demo_tour_music", musicOn ? "on" : "off"); } catch {} }, [musicOn]);
  const onRiskWin = useCallback((id) => setRiskWins((w) => (w.includes(id) ? w : [...w, id])), []);
  const riskBonus = riskWins.reduce((a, id) => a + (RISK_WIN_VALUE[id] || 0), 0);
  const resetPhase1 = useCallback(() => {
    setScoringPool([]);
    setPipeline([]);
    setApproved([]);
    setRiskWins([]);
    setPhase1Key((k) => k + 1);
  }, []);
  const onApproved = useCallback((m) => setApproved((a) => (a.includes(m.name) ? a : [...a, m.name])), []);
  const onboardMerchant = useMemo(() => {
    const lead = [...pipeline].sort((a, b) => (STAGE_RANK[b.stage] || 0) - (STAGE_RANK[a.stage] || 0))[0];
    return { ...DEFAULT_MERCHANT, ...(lead || {}), owner: "Jordan Reyes" };
  }, [pipeline]);

  const [intro, setIntro] = useState(null);
  const [showIntro, setShowIntro] = useState(true);
  const [introEditor, setIntroEditor] = useState(false);
  useEffect(() => {
    axios.get(`${API}/demos/${slug}/intro`).then(({ data }) => setIntro(data)).catch(() => setShowIntro(false));
  }, [slug]);
  const [showHype, setShowHype] = useState(false);
  const endIntro = useCallback(() => { setShowIntro(false); setShowHype(true); }, []);
  const closeHype = useCallback(() => setShowHype(false), []);
  const [showOutro, setShowOutro] = useState(false);
  const playOutro = useCallback(() => { setShowHype(false); setShowOutro(true); }, []);

  const [nexSavings, setNexSavings] = useState(null);
  useEffect(() => {
    axios.get(`${API}/demos/${slug}/nex-savings`).then(({ data }) => setNexSavings(data)).catch(() => {});
  }, [slug]);

  const hotCount = useMemo(
    () => scoringPool.filter((l) => tierOf(scoreLead(l, weights)) === "hot").length,
    [scoringPool, weights]
  );

  // Editable Phase 2-5 cards.  Fetched from the backend, which seeds
  // the 16 defaults on first access.  Admin controls (edit pencil +
  // "Add card" button) only render when a bearer token is present
  // in localStorage.
  const [cards, setCards]       = useState([]);
  const [cardsLoading, setCardsLoading] = useState(true);
  const [editing, setEditing]   = useState(null); // card obj or null
  const [addingPhase, setAddingPhase] = useState(null); // 2|3|4|5|null
  const [isAdmin] = useState(() => {
    try { return !!localStorage.getItem("nex_token"); } catch { return false; }
  });

  const loadCards = async () => {
    try {
      const { data } = await axios.get(`${API}/demos/${slug}/cards`);
      setCards(data.items || []);
    } catch { /* silent */ }
    finally { setCardsLoading(false); }
  };

  useEffect(() => { loadCards(); }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  const [tourLines, setTourLines] = useState({});
  const loadTourLines = () => axios.get(`${API}/demos/${slug}/tour-lines`)
    .then(({ data }) => setTourLines(Object.fromEntries((data.items || []).map((l) => [l.step, l]))))
    .catch(() => {});
  useEffect(() => { loadTourLines(); }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps
  const lineAudio = (step) => (tourLines[step]?.rendered ? tourAudioUrl(slug, step, tourLines[step].version) : null);
  const onLineSaved = (line) => setTourLines((m) => ({ ...m, [line.step]: line }));

  const tourScript = useMemo(() => VEROOGLE_TOUR_SCRIPT.map((s) => {
    const l = tourLines[s.step];
    const branded = { ...s, kicker: brandText(s.kicker, name), title: brandText(s.title, name), bullets: s.bullets?.map((b) => brandText(b, name)) };
    return {
      ...branded,
      caption: l ? l.text : brandText(s.caption, name),
      edited: l?.edited,
      audio: l?.rendered ? tourAudioUrl(slug, s.step, l.version) : null,
    };
  }), [tourLines, name, slug]);
  const cardCopy = useMemo(() => Object.fromEntries(cards.map((c) => [c.card_id, c])), [cards]);
  const brand = useMemo(() => ({ slug, name, accent, cardCopy, isAdmin, onEditCard: setEditing }), [slug, name, accent, cardCopy, isAdmin]);
  const session = useMemo(() => ({ leads: scoringPool.length, pipeline: pipeline.length, approved: approved.length, recovered: riskBonus }),
    [scoringPool.length, pipeline.length, approved.length, riskBonus]);

  const cardsByPhase = useMemo(() => {
    const by = { 2: [], 3: [], 4: [], 5: [] };
    cards.forEach((c) => { if (by[c.phase]) by[c.phase].push(c); });
    return by;
  }, [cards]);

  return (
    <DemoBrandProvider value={brand}>
    <DemoModeProvider value>
      <GuidedTourProvider script={tourScript} onComplete={playOutro}>
        <TourActionRunner onReset={resetPhase1} />
        <TourMusicBed url={intro?.bed_url ? `${process.env.REACT_APP_BACKEND_URL}${intro.bed_url}` : null} enabled={musicOn} />
        <TourControlStrip isAdmin={isAdmin} onRerecorded={loadTourLines} onReplayIntro={() => { setShowHype(false); setShowOutro(false); setShowIntro(true); }} onPlayFinale={playOutro} pulseStart={showHype} musicOn={musicOn} onToggleMusic={toggleMusic} />
        <div className="veroogle-scope flex flex-col gap-6" data-testid="veroogle-canvas" style={{ "--demo-accent": accent }}>
        {/* Scoped CSS matching the Nexengines Platform Demo chrome — same
            glass-card, same hover glow, same gradient-emerald highlight,
            same custom scrollbar. */}
        <style>{`
          .veroogle-scope .glass-card {
            background: rgba(17, 26, 46, 0.75);
            backdrop-filter: blur(12px);
            border: 1px solid rgba(255, 255, 255, 0.08);
            box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.37);
          }
          .veroogle-scope .glass-card:hover {
            border-color: ${accent}66;
            box-shadow: 0 12px 40px 0 ${accent}26;
          }
          .veroogle-scope .gradient-emerald {
            background: linear-gradient(135deg, #34d399 0%, #10b981 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
          }
          .veroogle-scope ::-webkit-scrollbar { width: 6px; height: 6px; }
          .veroogle-scope ::-webkit-scrollbar-track { background: #0c1322; }
          .veroogle-scope ::-webkit-scrollbar-thumb { background: #233352; border-radius: 3px; }
          .veroogle-scope ::-webkit-scrollbar-thumb:hover { background: ${accent}; }
        `}</style>

        {/* Metric Savings Bar — reactive to Phase 1 activity */}
        <VerooglMetricsBar
          nexSavings={nexSavings}
          scoringPool={scoringPool}
          pipeline={pipeline}
          hotCount={hotCount}
          approvedCount={approved.length}
          riskBonus={riskBonus}
        />

        {/* Phase 1 — the active build target */}
        <AccordionSection
          id={phaseMeta[0].id}
          kicker={phaseMeta[0].kicker}
          title={phaseMeta[0].title}
        >
          <div className="flex flex-col gap-5" key={phase1Key}>
            <LeadScrapingCard onPushToScoring={pushToScoring} />
            <LeadScoringCard
              scoringPool={scoringPool}
              onPushToPipeline={pushToPipeline}
              onWeightsChange={setWeights}
            />
            <LeadPipelineCard pipeline={pipeline} setPipeline={setPipeline} />
            <StatementAnalyzerCard />
          </div>
        </AccordionSection>

        {/* Phases 2-6 — interactive cards + editable "listening" stubs */}
        {phaseMeta.slice(1).map((p) => {
          const stubs = (cardsByPhase[p.phase] || []).filter((c) => !(INTERACTIVE_IDS[p.phase] || []).includes(c.card_id));
          return (
            <AccordionSection
              key={p.id}
              id={p.id}
              kicker={p.kicker}
              title={p.title}
              defaultOpen={false}
            >
              {p.phase === 2 && (
                <div className="flex flex-col gap-5 mb-5" key={`p2-${phase1Key}`} data-testid="veroogle-phase2-interactive">
                  <IntakeCard merchant={onboardMerchant} />
                  <DocVaultCard merchant={onboardMerchant} />
                  <KycCard merchant={onboardMerchant} />
                  <UnderwritingCard merchant={onboardMerchant} onApproved={onApproved} />
                </div>
              )}
              {p.phase === 3 && (
                <div className="flex flex-col gap-5 mb-5" key={`p3-${phase1Key}`} data-testid="veroogle-phase3-interactive">
                  <MidMonitorCard merchant={onboardMerchant} />
                  <FraudCard merchant={onboardMerchant} onCatch={onRiskWin} />
                  <DisputeCard onRecovered={onRiskWin} />
                  <ResidualCard />
                </div>
              )}
              {(p.phase === 4 || p.phase === 5) && (
                <div className="flex flex-col gap-5 mb-5" key={`p${p.phase}-${phase1Key}`} data-testid={`veroogle-phase${p.phase}-interactive`}>
                  {p.phase === 4
                    ? <Phase4Cards merchant={onboardMerchant} onWin={onRiskWin} />
                    : <Phase5Cards merchant={onboardMerchant} onWin={onRiskWin} />}
                </div>
              )}
              {p.phase === 6 && (
                <div className="flex flex-col gap-5 mb-5" key={`p6-${phase1Key}`} data-testid="veroogle-phase6-interactive">
                  <Phase6Cards merchant={onboardMerchant} />
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid={`veroogle-grid-phase${p.phase}`}>
                {stubs.map((s) => (
                  <PhaseStubCard
                    key={s.id}
                    id={s.card_id}
                    title={s.title}
                    hint={s.hint}
                    Icon={resolveIcon(s.icon_key)}
                    editable={isAdmin}
                    onEdit={() => setEditing(s)}
                  />
                ))}
                {isAdmin && (
                  <button
                    type="button"
                    onClick={() => setAddingPhase(p.phase)}
                    data-testid={`veroogle-add-card-phase${p.phase}`}
                    className="glass-card rounded-xl px-4 py-4 flex flex-col items-center justify-center gap-2 text-slate-400 hover:text-brand-cyan border-dashed transition-all duration-300 hover:-translate-y-1 min-h-[128px] border-2 border-white/10 hover:border-brand-cyan/60"
                  >
                    <Plus className="w-5 h-5" />
                    <span className="font-mono text-[10px] tracking-[0.18em] uppercase">Add card</span>
                  </button>
                )}
                {!isAdmin && stubs.length === 0 && !cardsLoading && !INTERACTIVE_IDS[p.phase] && (
                  <div className="col-span-full text-slate-500 font-mono text-[10px] tracking-[0.18em] uppercase text-center py-6">
                    No cards in this phase.
                  </div>
                )}
              </div>
            </AccordionSection>
          );
        })}
      </div>
        <TourOverlay isAdmin={isAdmin} onLineSaved={onLineSaved} />
        <SoundGate color="#10B981" />
        {showIntro && intro && Object.keys(tourLines).length > 0 && (
          <CinematicIntro
            intro={intro}
            audioUrl={lineAudio("IN")}
            phases={introPhases}
            musicUrl={intro.music_url ? `${process.env.REACT_APP_BACKEND_URL}${intro.music_url}` : null}
            isAdmin={isAdmin}
            onEdit={() => { setShowIntro(false); setIntroEditor(true); }}
            onDone={endIntro}
          />
        )}
        {showHype && !showIntro && tourLines.HY && (
          <HypeCard
            key={tourLines.HY.version}
            line={tourLines.HY}
            audioUrl={lineAudio("HY")}
            bedUrl={intro?.bed_url ? `${process.env.REACT_APP_BACKEND_URL}${intro.bed_url}` : null}
            isAdmin={isAdmin}
            onLineSaved={onLineSaved}
            onClose={closeHype}
          />
        )}
        {isAdmin && intro && (
          <IntroEditorDialog
            open={introEditor}
            onOpenChange={setIntroEditor}
            slug={slug}
            intro={intro}
            narration={tourLines.IN}
            onSaved={setIntro}
            onLineSaved={onLineSaved}
          />
        )}
        {showOutro && (
          <CinematicOutro
            name={name}
            logoUrl={intro?.logo_url}
            accent={accent}
            phases={phaseMeta.map((p) => p.title)}
            session={session}
            savings={{ annual: nexSavings?.annual || 0 }}
            audioUrl={lineAudio("OUT")}
            musicUrl={intro?.outro_music_url ? `${process.env.REACT_APP_BACKEND_URL}${intro.outro_music_url}` : null}
            line={tourLines.OUT}
            isAdmin={isAdmin}
            onLineSaved={onLineSaved}
            onReplayTour={() => { setShowOutro(false); resetPhase1(); }}
            onClose={() => setShowOutro(false)}
          />
        )}
      </GuidedTourProvider>

      {(editing || addingPhase !== null) && (
        <CardEditModal
          card={editing}
          defaultPhase={addingPhase ?? editing?.phase ?? 2}
          onClose={() => { setEditing(null); setAddingPhase(null); }}
          onSaved={() => loadCards()}
          onDeleted={() => loadCards()}
        />
      )}
    </DemoModeProvider>
    </DemoBrandProvider>
  );
}
